import type { FunctionComponentWithChildren } from '@bonsai-components/utility-types';
import { semanticColors, typography } from '@moderneinc/design-system-tokens';
import {
  Box,
  CardHeader,
  cardHeaderClasses,
  IconButton,
  SvgIcon
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import { type FunctionComponent, useContext } from 'react';
import {
  type DownloadManifest,
  DownloadsContext
} from '../../contexts/downloads.context';
import { isDownloadInProgress } from '../../helpers/download-manifest.helper';
import {
  ModArrowCircleDown,
  ModArrowCircleUp,
  ModCancelIcon,
  ModDoubleArrowIcon
} from '../../icons/moderne-icons';
import {
  CenteredBox,
  FlexBox
} from '../styled-components/layouts/layouts.styled';
import { ExpandCollapse } from '../utilities/expand-collapse/expand-collapse.component';
import { Downloadable, DownloadStateIcon } from './downloadable.component';

type DownloadManagerProps = {
  downloadManifests?: DownloadManifest[];
};

const DownloadManagerContainer: FunctionComponentWithChildren = ({
  children
}) => {
  const { minimized, toggleMinimize, downloads, removeDownload } =
    useContext(DownloadsContext);

  const confirm = useConfirm();

  const handleDismissDownloads = () =>
    confirm({
      title: 'Dismiss downloads',
      description: 'Are you sure you want to remove all downloads?'
    }).then(({ confirmed }) => confirmed && removeAllDownloads());

  const removeAllDownloads = () => {
    downloads.forEach((download) => {
      removeDownload(download.indexDbId);
    });
  };

  const downloadsInProgress = (
    downloads?.filter((download) => isDownloadInProgress(download.state)) || []
  ).length;

  const completedWithFailures =
    !downloadsInProgress &&
    (
      downloads?.filter((download) => {
        return download.state === 'FAILURE';
      }) || []
    ).length;

  const overallDownloadState =
    downloadsInProgress > 0
      ? 'PENDING'
      : completedWithFailures
        ? 'FAILURE'
        : 'SUCCESS';

  return (
    <Box
      sx={{
        minWidth: 320,
        maxWidth: 500,
        overflow: 'hidden',
        bgcolor: semanticColors.color.surface.raised,
        boxShadow: 6,
        paddingX: 2.5,
        borderColor: semanticColors.color.border.primary,
        borderTopLeftRadius: '1rem',
        borderTopRightRadius: '1rem',
        pointerEvents: 'auto'
      }}
    >
      <CardHeader
        sx={{
          [`& .${cardHeaderClasses.action}`]: {
            margin: 0,
            alignSelf: 'center',
            marginRight: 0.6
          },
          alignItems: 'center',
          justifyContent: 'center',
          px: 0,
          pt: 2,
          pb: 0
        }}
        avatar={
          <FlexBox
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: semanticColors.color.surface.raisedHeader,
              borderRadius: '50%',
              width: '36px',
              height: '36px'
            }}
          >
            {overallDownloadState === 'PENDING' ? (
              <ModDoubleArrowIcon
                color="primary"
                sx={{
                  fontSize: (theme) => theme.typography.pxToRem(25)
                }}
              />
            ) : (
              <CenteredBox
                sx={{
                  flexDirection: 'column'
                }}
              >
                <DownloadStateIcon state={overallDownloadState} />
              </CenteredBox>
            )}
          </FlexBox>
        }
        title="Preparing downloads..."
        slotProps={{
          title: {
            fontWeight: typography.fontWeight.semiBold,
            fontSize: '.875rem'
          },
          subheader: { variant: 'caption' }
        }}
        subheader={
          downloadsInProgress > 0
            ? `${downloadsInProgress} in progress`
            : 'Completed'
        }
        action={
          <Box
            sx={{
              pr: 0.5
            }}
          >
            <IconButton
              onClick={handleDismissDownloads}
              size="small"
              title="Dismiss all downloads"
            >
              <SvgIcon
                component={ModCancelIcon}
                inheritViewBox
                color={'secondary'}
                sx={{ fontSize: (theme) => theme.typography.pxToRem(32.5) }}
              />
            </IconButton>
            <ExpandCollapse
              title="Expand or collapse downloads"
              variant="icon"
              isExpanded={!minimized}
              sx={{ padding: 0 }}
              expandIcon={
                <ModArrowCircleDown
                  sx={{
                    fontSize: (theme) => theme.typography.pxToRem(32.5)
                  }}
                />
              }
              collapseIcon={
                <ModArrowCircleUp
                  sx={{
                    fontSize: (theme) => theme.typography.pxToRem(32.5)
                  }}
                />
              }
              onClick={toggleMinimize}
              color="inherit"
            />
          </Box>
        }
      />
      {/* Download rows perform the polling so must be rendered or polling does not happen when collapsed */}
      <Box
        sx={{
          height: minimized ? 0 : undefined,
          visibility: minimized ? 'hidden' : 'visible'
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export const DownloadManager: FunctionComponent<DownloadManagerProps> = ({
  downloadManifests
}) => (
  <DownloadManagerContainer>
    {downloadManifests.map((manifest) => (
      <Downloadable
        key={manifest.indexDbId ?? manifest.id}
        manifest={manifest}
      />
    ))}
  </DownloadManagerContainer>
);
